/**
 * Terms of Service Page
 * World-class Terms of Service with comprehensive legal coverage
 * Public legal page accessible without authentication
 */

import LegalPageLayout from '@/components/legal/LegalPageLayout';
import { LEGAL_LAST_UPDATED, CANONICAL_DOMAIN } from '@/constants/legalVersions';

export default function TermsPage() {
  return (
    <LegalPageLayout 
      title="Terms of Service" 
      lastUpdated={LEGAL_LAST_UPDATED.terms}
      metaDescription="Terms of Service for FinList - Read about your rights and responsibilities when using our personal finance and household management platform."
    >
      {/* Section 1: Introduction */}
      <section>
        <h2>1. Introduction</h2>
        <p>
          Welcome to <strong>FinList</strong> ("FinList", "we", "our", or "us"). FinList is a personal 
          finance and household management platform designed to help users manage budgets, shopping lists, 
          savings goals, tasks, and related financial insights.
        </p>
        <p>
          By accessing or using FinList via{' '}
          <a href={CANONICAL_DOMAIN} className="font-medium">{CANONICAL_DOMAIN}</a>, mobile applications, 
          or any related services (collectively, the "Service"), you agree to be bound by these Terms of 
          Service (the "Terms").
        </p>
        <p>
          <strong>If you do not agree to these Terms, you must not use the Service.</strong>
        </p>
      </section>

      {/* Section 2: Eligibility */}
      <section>
        <h2>2. Eligibility</h2>
        <p>To use FinList, you must:</p>
        <ul>
          <li>Be at least <strong>18 years of age</strong></li>
          <li>Have the legal capacity to enter into a binding agreement</li>
          <li>Not be prohibited from using the Service under applicable law</li>
        </ul>
        <p>
          By using FinList, you represent and warrant that you meet all eligibility requirements.
        </p>
      </section>

      {/* Section 3: Account Registration */}
      <section>
        <h2>3. Account Registration</h2>
        <p>To access certain features of the Service, you must create an account. When registering, you agree to:</p>
        <ul>
          <li>Provide accurate, complete, and current information</li>
          <li>Maintain the security and confidentiality of your login credentials</li>
          <li>Promptly update your account information if it changes</li>
          <li>Accept responsibility for all activities that occur under your account</li>
        </ul>
        <p>
          You must notify us immediately if you suspect unauthorized access to your account.
        </p>
      </section>

      {/* Section 4: Acceptable Use */}
      <section>
        <h2>4. Acceptable Use</h2>
        <p>
          You agree to use FinList only for lawful purposes and in accordance with these Terms.
        </p>
        <p><strong>You must NOT:</strong></p>
        <ul>
          <li>Use the Service for fraudulent, misleading, or illegal activities</li>
          <li>Attempt to gain unauthorized access to systems, data, or other user accounts</li>
          <li>Reverse-engineer, decompile, or scrape any part of the platform</li>
          <li>Upload malicious code, viruses, or harmful content</li>
          <li>Interfere with or disrupt the integrity or performance of the Service</li>
          <li>Impersonate another person or entity</li>
        </ul>
      </section>

      {/* Section 5: Financial Disclaimer */}
      <section>
        <h2>5. Financial Disclaimer</h2>
        <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 my-4">
          <p className="font-semibold text-amber-800 dark:text-amber-200">⚠️ Important Notice</p>
          <p className="text-amber-700 dark:text-amber-300">
            FinList does <strong>NOT</strong> provide financial, investment, legal, or tax advice.
          </p>
        </div>
        <p>
          All insights, analytics, AI-generated suggestions, budgeting tools, and recommendations 
          are provided <strong>for informational purposes only</strong>.
        </p>
        <ul>
          <li>You are solely responsible for all financial decisions made using the Service</li>
          <li>We do not guarantee the accuracy, completeness, or suitability of any financial insights</li>
          <li>You should consult qualified professionals for financial, legal, or tax advice</li>
        </ul>
      </section>

      {/* Section 6: AI Features & Automation */}
      <section>
        <h2>6. AI Features &amp; Automation</h2>
        <p>FinList incorporates AI-powered features to enhance your experience, including:</p>
        <ul>
          <li>Automatic categorization of transactions and items</li>
          <li>Smart insights and spending pattern analysis</li>
          <li>AI-generated summaries and recommendations</li>
          <li>Natural language processing for task and list management</li>
        </ul>
        <p><strong>Important limitations:</strong></p>
        <ul>
          <li>AI outputs may be imperfect and should not replace professional advice</li>
          <li>You remain responsible for reviewing and validating all AI-generated content</li>
          <li>AI features may be updated, modified, or discontinued at our discretion</li>
        </ul>
      </section>

      {/* Section 7: Subscriptions & Payments */}
      <section>
        <h2>7. Subscriptions &amp; Payments</h2>
        <p>FinList offers both free and premium subscription plans:</p>
        <ul>
          <li>Pricing, billing cycles, and included features are disclosed at the point of purchase</li>
          <li>Payments are securely processed via third-party providers (e.g., Stripe)</li>
          <li>Subscriptions automatically renew unless cancelled before the renewal date</li>
          <li>You may cancel your subscription at any time through your account settings</li>
        </ul>
        <p>
          Refunds, where applicable, are governed by applicable consumer protection laws and our refund policy.
        </p>
      </section>

      {/* Section 8: Third-Party Services */}
      <section>
        <h2>8. Third-Party Services</h2>
        <p>
          FinList may integrate with or link to third-party services, including:
        </p>
        <ul>
          <li>Payment processors (e.g., Stripe)</li>
          <li>Cloud infrastructure providers (e.g., Firebase, Google Cloud)</li>
          <li>AI service providers (e.g., OpenAI)</li>
          <li>Analytics platforms</li>
        </ul>
        <p>
          We are not responsible for third-party services, content, or practices. Your use of third-party 
          services is governed by their respective terms and privacy policies.
        </p>
      </section>

      {/* Section 9: Data & Content Ownership */}
      <section>
        <h2>9. Data &amp; Content Ownership</h2>
        <p><strong>Your data belongs to you.</strong></p>
        <ul>
          <li>You retain full ownership of all personal data and content you submit to FinList</li>
          <li>You grant FinList a limited, non-exclusive, royalty-free license to process your data solely to provide and improve the Service</li>
          <li>We do <strong>NOT</strong> sell your personal data to third parties</li>
          <li>You may export or delete your data at any time</li>
        </ul>
      </section>

      {/* Section 10: Account Suspension & Termination */}
      <section>
        <h2>10. Account Suspension &amp; Termination</h2>
        <p>We reserve the right to suspend or terminate your account if:</p>
        <ul>
          <li>You violate these Terms of Service</li>
          <li>We suspect fraud, abuse, or malicious activity</li>
          <li>Required by law, regulation, or legal process</li>
          <li>Your account has been inactive for an extended period</li>
        </ul>
        <p>
          You may terminate your account at any time by contacting us or using the account deletion 
          feature in Settings.
        </p>
      </section>

      {/* Section 11: Service Availability */}
      <section>
        <h2>11. Service Availability</h2>
        <p>FinList is provided on an <strong>"as is"</strong> and <strong>"as available"</strong> basis.</p>
        <ul>
          <li>We strive for high availability but do not guarantee uninterrupted service</li>
          <li>We may update, modify, or discontinue features without prior notice</li>
          <li>Scheduled maintenance will be communicated when possible</li>
          <li>We are not liable for service interruptions beyond our reasonable control</li>
        </ul>
      </section>

      {/* Section 12: Limitation of Liability */}
      <section>
        <h2>12. Limitation of Liability</h2>
        <p>To the maximum extent permitted by applicable law:</p>
        <ul>
          <li>
            FinList shall not be liable for any indirect, incidental, special, consequential, 
            or punitive damages arising from your use of the Service
          </li>
          <li>
            Our total aggregate liability shall not exceed the amount you have paid to FinList 
            in the <strong>12 months</strong> preceding the claim
          </li>
          <li>
            We are not liable for any loss of data, profits, or business opportunities
          </li>
        </ul>
        <p>
          Some jurisdictions do not allow limitations on liability, so these limitations may not apply to you.
        </p>
      </section>

      {/* Section 13: Indemnification */}
      <section>
        <h2>13. Indemnification</h2>
        <p>
          You agree to indemnify, defend, and hold harmless FinList, its officers, directors, employees, 
          and agents from any claims, damages, losses, or expenses (including reasonable legal fees) 
          arising from:
        </p>
        <ul>
          <li>Your use or misuse of the Service</li>
          <li>Your violation of these Terms</li>
          <li>Your violation of any applicable law or regulation</li>
          <li>Your infringement of any third-party rights</li>
        </ul>
      </section>

      {/* Section 14: Governing Law & Jurisdiction */}
      <section>
        <h2>14. Governing Law &amp; Jurisdiction</h2>
        <p>
          These Terms are governed by and construed in accordance with the laws of the 
          <strong> United Kingdom</strong>, without regard to conflict-of-law principles.
        </p>
        <p>
          Any disputes arising from these Terms shall be subject to the exclusive jurisdiction 
          of the courts of England and Wales.
        </p>
      </section>

      {/* Section 15: Changes to Terms */}
      <section>
        <h2>15. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time to reflect changes in our practices, 
          legal requirements, or Service features.
        </p>
        <ul>
          <li>Material changes will be communicated via email or in-app notification</li>
          <li>Continued use of FinList after changes constitutes acceptance of the updated Terms</li>
          <li>If you do not agree to the updated Terms, you must stop using the Service</li>
        </ul>
      </section>

      {/* Section 16: Contact Us */}
      <section>
        <h2>16. Contact Us</h2>
        <p>For questions or concerns regarding these Terms of Service, please contact us:</p>
        <p className="text-lg">
          <a href="mailto:support@getfinlist.com" className="font-medium">
            📧 support@getfinlist.com
          </a>
        </p>
      </section>

      <hr className="my-10 border-gray-300 dark:border-slate-600" />

      {/* Appendix A: App Store Compliance */}
      <section>
        <h2>Appendix A: App Store Compliance</h2>
        
        <h3>A.1 Apple App Store</h3>
        <p>If you access FinList through the Apple App Store:</p>
        <ul>
          <li>These Terms are an agreement between you and FinList, not Apple Inc.</li>
          <li>Apple is not responsible for the Service, its content, or support</li>
          <li>Apple has no obligation to provide maintenance or support services</li>
          <li>In the event of a failure, Apple's maximum liability is limited to refunding the purchase price (if any)</li>
          <li>Apple and its subsidiaries are third-party beneficiaries of these Terms and may enforce them</li>
        </ul>

        <h3>A.2 Google Play Store</h3>
        <p>If you access FinList through the Google Play Store:</p>
        <ul>
          <li>Google LLC is not responsible for the Service, content, or user support</li>
          <li>Payments and subscriptions may be managed through Google Play Billing where applicable</li>
          <li>Google Play's terms of service also apply to your use of the Service</li>
        </ul>
      </section>

      <hr className="my-10 border-gray-300 dark:border-slate-600" />

      {/* Appendix B: Feature-Specific Terms */}
      <section>
        <h2>Appendix B: Feature-Specific Terms</h2>

        <h3>B.1 Shared Shopping Lists</h3>
        <p>FinList allows you to share shopping lists with other users:</p>
        <ul>
          <li>You may share lists with registered users or via shareable links</li>
          <li>Shared recipients may have read-only or collaborative access depending on your settings</li>
          <li>You are responsible for managing who has access to your shared content</li>
          <li>FinList is not responsible for misuse of shared content by recipients</li>
        </ul>

        <h3>B.2 Public or Shareable Links</h3>
        <p>For convenience, FinList allows you to generate shareable links:</p>
        <ul>
          <li>Anyone with access to a shareable link may view the shared content</li>
          <li>Links may be time-limited, password-protected, or revocable</li>
          <li>You are responsible for distributing links only to intended recipients</li>
        </ul>

        <h3>B.3 Household &amp; Collaborative Features</h3>
        <p>FinList supports multi-user household management:</p>
        <ul>
          <li>The account owner is responsible for permissions granted to household members</li>
          <li>Household members may view, edit, or delete shared data based on permissions</li>
          <li>Disputes between household members must be resolved independently</li>
        </ul>
      </section>

      <hr className="my-10 border-gray-300 dark:border-slate-600" />

      {/* Appendix C: Regional Compliance */}
      <section>
        <h2>Appendix C: International Use &amp; Regional Compliance</h2>
        <p>
          FinList is designed for users in the <strong>United Kingdom, European Union, Africa, 
          United States, and other regions worldwide</strong>.
        </p>
        <p>We comply with applicable data protection and consumer laws, including:</p>
        <ul>
          <li><strong>UK GDPR</strong> – United Kingdom General Data Protection Regulation</li>
          <li><strong>EU GDPR</strong> – European Union General Data Protection Regulation</li>
          <li><strong>CCPA/CPRA</strong> – California Consumer Privacy Act / California Privacy Rights Act</li>
          <li><strong>POPIA</strong> – Protection of Personal Information Act (South Africa)</li>
          <li>Other applicable regional data protection regulations</li>
        </ul>
        <p>
          Where local laws require additional protections or disclosures, FinList will apply them accordingly.
        </p>
      </section>
    </LegalPageLayout>
  );
}
